import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { APP_URL_HOME, APP_URL_PROFILE_BIRTH_CHART } from '../../const';
import { getAuthModalState, toggleAuthModal } from '../../store/slices/authSlice';
import Spinner from '../common/Spinner';

// Signed-out visitors stay on the URL they asked for and sign in through the
// auth modal over `fallback` (the signed-out home page); closing it without
// signing in sends them home.
const ProtectedRoute = ({ user, children, fallback = null }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const showAuthModal = useSelector(getAuthModalState);
    const openedRef = useRef(false);
    const seenOpenRef = useRef(false);
    // OAuth return: useAuth is storing the token and fetching the user. Don't
    // open the modal or navigate (that would drop the jwt param).
    const awaitingOAuth = !user && searchParams.has('jwt');

    useEffect(() => {
        if (user || awaitingOAuth || openedRef.current) return;
        openedRef.current = true;
        const context = pathname.endsWith(`/${APP_URL_PROFILE_BIRTH_CHART}`) ? 'birthChart' : 'protectedRoute';
        dispatch(toggleAuthModal({ trigger: true, context, autoOpen: true }));
    }, [user, awaitingOAuth, pathname, dispatch]);

    // Only a modal we saw open and then close counts as a dismissal; right after
    // dispatching, this render still holds the pre-open (false) value.
    useEffect(() => {
        if (showAuthModal) seenOpenRef.current = true;
        else if (!user && seenOpenRef.current) navigate(APP_URL_HOME, { replace: true });
    }, [user, showAuthModal, navigate]);

    if (user && children) return children;
    if (awaitingOAuth) return <Spinner />;
    return fallback || <Outlet />;
};

export default ProtectedRoute;
